/* Organization permission hook — the single source of truth for capability checks.
   Resolves from OrganizationRuntimeContext. SUPER_ADMIN always gets full access.

   Usage:
     const perm = usePermissions();
     perm.has('org.members.invite')      // boolean
     perm.hasAll(['org.roles.create', 'org.roles.edit'])  // boolean
     perm.hasAny(['org.settings.view'])  // boolean
     perm.isSA                           // boolean — platform super admin
     perm.flags.canManageMembers         // boolean shorthand flag
     perm.deny('org.members.invite')     // DenialInfo | null
*/
import { useOrgPermissions, useCurrentOrgUser } from '../OrganizationRuntimeContext.jsx';

// LOW-15: warn once in dev when globalThis.useAuth is missing — SA elevation silently disabled
let _authWarned = false;

export function usePermissions() {
  /* runtime.permissions = perms.flags (the flags object directly, not {capabilities,flags}).
     runtime.currentUser.capabilities = the capabilities string array.
     Reading capabilities from permissions?.capabilities would always yield []. */
  const permFlags   = useOrgPermissions();    // flags object: { canManageMembers, ... }
  const currentUser = useCurrentOrgUser();    // { capabilities: [...], orgRole, ... }
  const auth        = typeof globalThis.useAuth === 'function' ? globalThis.useAuth() : {};
  if (import.meta.env?.DEV && typeof globalThis.useAuth !== 'function' && !_authWarned) {
    console.warn('[usePermissions] globalThis.useAuth not registered — SA elevation disabled. Register it during app init.');
    _authWarned = true;
  }
  const isSA  = auth?.user?.platformRole === 'SUPER_ADMIN';
  const caps  = Array.isArray(currentUser?.capabilities) ? currentUser.capabilities : [];
  const flags = permFlags ?? {};

  /* Bidirectional resolver: handles coarse vs granular key mismatch.
     Runtime returns granular keys (org.branding.view). Code may check coarse keys (org.branding).
     - child satisfies parent: caps has 'org.branding.view' → has('org.branding') = true
     - parent satisfies child: caps has 'org.branding'      → has('org.branding.view') = true */
  const matchesCap = (cap, userCap) =>
    userCap === cap ||
    userCap.startsWith(cap + '.') ||
    cap.startsWith(userCap + '.');

  const has = (cap) => {
    if (!cap) return true;
    if (isSA) return true;
    return caps.some(c => matchesCap(cap, c));
  };

  const hasAll = (capList) => {
    if (!capList?.length) return true;
    if (isSA) return true;
    return capList.every(cap => caps.some(c => matchesCap(cap, c)));
  };

  const hasAny = (capList) => {
    if (!capList?.length) return true;
    if (isSA) return true;
    return capList.some(cap => caps.some(c => matchesCap(cap, c)));
  };

  const deny = (cap) => {
    if (has(cap)) return null;
    return { cap, reason: 'insufficient_permission' };
  };

  return { has, hasAll, hasAny, isSA, flags, deny };
}

/* Workspace-scoped permission object for a given wsRole.
   Not a hook — pure function. Returns object compatible with WsCapabilityButton. */
const WS_ADMIN_CAPS = new Set([
  'workspace.view', 'workspace.settings', 'membership.view',
  'membership.manage', 'crm.use', 'crm.manage',
]);
const WS_USER_CAPS = new Set([
  'workspace.view', 'membership.view', 'crm.use',
]);

export function useWsPermissions(wsRole, _wsCustomRole) {
  const isAdmin = wsRole === 'WORKSPACE_ADMIN';
  const capSet  = isAdmin ? WS_ADMIN_CAPS : WS_USER_CAPS;
  const has = (cap) => {
    if (!cap) return true;
    if (isAdmin) return true;
    return capSet.has(cap);
  };
  return { isAdmin, has };
}
